<template>
    <div>
        <el-dialog align-center v-model="state.dialogVisible" :title="dialogTitle" width="800" draggable>
            <div class="userAdd_man">
                <el-form ref="ruleForm" label-width="auto" :model="state.form" :rules="rules" class="userFrom">
                    <el-col :span="12" style="margin-bottom: 20px;">
                        <el-form-item :label="t('field.realName')">
                            {{ rowInfo?.name || '-' }}
                        </el-form-item>
                    </el-col>
                    <el-col :span="12" style="margin-bottom: 20px;">
                        <el-form-item :label="t('attendance.makeupDate')">
                            {{ formatAuditDate(rowInfo?.makeup_date) }}
                        </el-form-item>
                    </el-col>
                    <el-col :span="12" style="margin-bottom: 20px;">
                        <el-form-item :label="t('attendance.makeupTime')">
                            {{ formatAuditTime(rowInfo?.makeup_time) }}
                        </el-form-item>
                    </el-col>
                    <el-col :span="12" style="margin-bottom: 20px;">
                        <el-form-item :label="t('attendance.makeupType')">
                            {{ renderMakeupType(rowInfo?.makeup_type) }}
                        </el-form-item>
                    </el-col>
                    <el-col v-if="rowInfo?.makeup_type === 'ot'" :span="12" style="margin-bottom: 20px;">
                        <el-form-item :label="t('attendance.overtimeDuration')">
                            {{ rowInfo?.overtime_hours != null ? rowInfo.overtime_hours + t('attendanceDialog.hourSuffix') : '-' }}
                        </el-form-item>
                    </el-col>
                    <el-col :span="24" style="margin-bottom: 20px;">
                        <el-form-item :label="t('attendance.anomalyReason')">
                            {{ rowInfo?.reason || '-' }}
                        </el-form-item>
                    </el-col>
                    <el-col :span="24" style="margin-bottom: 20px;" v-if="rowInfo?.photos">
                        <el-form-item :label="t('attendanceDialog.relatedPhotos')">
                            <hc-oss-image-list :value="rowInfo.photos" />
                        </el-form-item>
                    </el-col>

                    <el-divider></el-divider>

                    <el-col :span="24" style="margin-bottom: 20px;">
                        <el-form-item :label="t('attendanceDialog.auditRemark')" prop="remark">
                            <el-input
                                v-model="state.form.remark"
                                type="textarea"
                                :rows="3"
                                :placeholder="t('attendanceDialog.auditRemarkPlaceholder')"
                            ></el-input>
                        </el-form-item>
                    </el-col>

                    <el-col :span="24" style="margin-bottom: 20px;">
                        <el-form-item :label="t('attendanceDialog.auditResult')" prop="action">
                            <el-radio-group v-model="state.form.action">
                                <el-radio :label="auditAction.pass">{{ t('attendance.pass') }}</el-radio>
                                <el-radio :label="auditAction.reject">{{ t('attendance.reject') }}</el-radio>
                            </el-radio-group>
                        </el-form-item>
                    </el-col>
                </el-form>
            </div>
            <template #footer>
                <div class="dialog-footer">
                    <el-button @click="state.dialogVisible = false">{{ t('common.cancel') }}</el-button>
                    <el-button type="primary" @click="submit">{{ t('common.confirm') }}</el-button>
                </div>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { reactive, ref, defineEmits, defineProps, defineExpose, computed } from "vue";
import { ElMessage } from "element-plus";
import { auditApi } from "@/api/attendance/anomaly";
import { formatDisplayDate, formatDisplayTime } from "@/utils/dateTime.js";
import { isTenantAdminRole } from "@/utils/adminMenuPermission.js";
import { usePageI18n, t } from "@/composables/usePageI18n.js";

const emit = defineEmits(['refresh']);
const props = defineProps({
    title: { type: String, default: '' },
});

const dialogTitle = computed(() => props.title || t('attendanceDialog.anomalyAuditTitle'));

const rules = reactive({ remark: [] });

const state = reactive({
    dialogVisible: false,
    form: { id: 0, action: 'captain_pass', remark: '' },
});

const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
const currentUser = userInfo?.users || userInfo?.user?.users || userInfo || {};
const roleId = currentUser?.roleId || currentUser?.role_id || 0;
const userId = currentUser?.id || 0;
const isAdmin = computed(() => isTenantAdminRole(Number(roleId)));

const auditAction = computed(() => isAdmin.value
    ? { pass: 'admin_pass', reject: 'admin_reject' }
    : { pass: 'captain_pass', reject: 'captain_reject' });

const rowInfo = ref(null);

const open = (row) => {
    state.dialogVisible = true;
    rowInfo.value = row;
    state.form.id = row.id;
    state.form.remark = '';
    state.form.action = isAdmin.value ? 'admin_pass' : 'captain_pass';
};

const ruleForm = ref(null);

const submit = () => {
    ruleForm.value.validate((valid) => {
        if (!valid) return false;
        if (!userId || userId === 0) {
            ElMessage.error(t('attendance.cannotGetCurrentUser'));
            return;
        }
        auditApi(JSON.stringify({
            id: state.form.id,
            auditor_id: userId,
            action: state.form.action,
            remark: state.form.remark || '',
        })).then(res => {
            if (res?.code === 200) {
                ElMessage.success(t('attendanceDialog.auditSuccess'));
                state.dialogVisible = false;
                state.form = { id: 0, action: 'captain_pass', remark: '' };
                emit('refresh');
            } else {
                ElMessage.error(res?.msg || t('attendanceDialog.auditFailed'));
            }
        }).catch(() => ElMessage.error(t('attendanceDialog.auditFailed')));
    });
};

const renderMakeupType = (type) => ({
    on_machine: t('attendance.onMachine'),
    off_machine: t('attendance.offMachine'),
    ot: t('attendance.ot'),
    zhongzhi: t('attendance.zhongzhi'),
}[type] || type);

const formatAuditDate = (date) => formatDisplayDate(date) || '-';

const formatAuditTime = (time) => formatDisplayTime(time) || '-';

usePageI18n(() => {});

defineExpose({ open, state });
</script>

<style lang="less" scoped>
.userAdd_man {
    min-height: 200px;
    max-height: 600px;
    overflow: auto;
    padding: 0 30px;
}
.userFrom {
    display: flex;
    flex-wrap: wrap;
}
.el-form-item {
    margin-bottom: 0;
}
.record-img {
    width: 160px;
    height: 160px;
    border-radius: 6px;
    object-fit: cover;
    margin-left: 10px;
    margin-top: 10px;
}
</style>
